import { memo, useCallback, useEffect, useState } from 'react';
import classes from 'classnames';
import styles from 'components/DocumentUpload/DocumentUpload.module.scss';

import CheckIcon from 'assets/icons/check-circle.svg?react';
import HazardIcon from 'assets/icons/hazard.svg?react';
import IdBackIcon from 'assets/icons/id-back.svg?react';
import IdFrontIcon from 'assets/icons/id-front.svg?react';
import PlusIcon from 'assets/icons/plus-circle.svg?react';
import SelfieIcon from 'assets/icons/selfie.svg?react';
import TrashIcon from 'assets/icons/trash.svg?react';
import DocumentIcon from 'assets/icons/document.svg?react';

import Typography from 'components/Typography/Typography';
import { useTranslation } from 'react-i18next';

export type Props = {
  type: keyof typeof UPLOAD_TYPE;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  error?: string;
  isTouched?: boolean;
  value?: Blob | null;
  onDelete: (name: string) => void;
  dataTest?: string;
  name: string;
  containerClassName?: string;
  customSubtitle?: string;
  customTitle?: string;
};

const UPLOAD_TYPE = {
  ID_FRONT: {
    icon: IdFrontIcon,
    title: 'document_upload:id_front_title',
    subtitle: 'document_upload:id_front_subtitle',
  },
  ID_BACK: {
    icon: IdBackIcon,
    title: 'document_upload:id_back_title',
    subtitle: 'document_upload:id_back_subtitle',
  },
  SELFIE: {
    icon: SelfieIcon,
    title: 'document_upload:selfie_title',
    subtitle: 'document_upload:selfie_subtitle',
  },
  INCOME_PROOF: {
    icon: DocumentIcon,
    title: 'document_upload:income_proof_title',
  },
  OTHER_DOCUMENT: {
    icon: DocumentIcon,
    title: 'document_upload:other_document_title',
    subtitle: 'document_upload:other_document_subtitle',
  },
  ADDITIONAL_DOCUMENT: {
    icon: DocumentIcon,
    title: 'document_upload:other_document_title',
    subtitle: 'document_upload:other_document_subtitle',
  },
} as const;

const getBase64FromFile = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      }
    });

    reader.addEventListener('error', () => {
      reject();
    });

    reader.readAsDataURL(file);
  });
};

type UploadSubtitle = Extract<
  (typeof UPLOAD_TYPE)[keyof typeof UPLOAD_TYPE],
  { subtitle: string }
>['subtitle'];

const DocumentUpload = memo(function DocumentUpload({
  type,
  value,
  onChange,
  className,
  error,
  isTouched,
  onDelete,
  dataTest = 'documentUpload',
  name,
  containerClassName,
  customSubtitle,
  customTitle,
}: Props) {
  const [img, setImg] = useState('');
  const { t } = useTranslation();
  const Icon = UPLOAD_TYPE[type].icon;
  const showError = isTouched && error;

  useEffect(() => {
    const createImg = async () => {
      if (!value) return;

      const convertedImg = await getBase64FromFile(value);
      setImg(convertedImg);
    };

    createImg();
  }, [value]);

  const handleDelete = useCallback(() => {
    if (!onDelete) return;

    onDelete(name);
  }, [name, onDelete]);

  // Not every upload type defines a subtitle
  const uploadType: { title: string; subtitle?: UploadSubtitle } =
    UPLOAD_TYPE[type];
  const subtitleText =
    customSubtitle || (uploadType?.subtitle ? t(uploadType.subtitle) : '');

  return (
    <div className={className}>
      <div
        className={classes(
          styles.container,
          containerClassName,
          showError && styles.error,
        )}
        data-test={dataTest}
      >
        {value ? (
          <div
            style={{
              backgroundImage: `url(${img})`,
            }}
            className={styles.preview}
          >
            {isTouched && error ? (
              <HazardIcon
                className={styles['overlayed-plus']}
                data-test={`${dataTest}-icon-hazard`}
              />
            ) : (
              <CheckIcon
                className={styles['overlayed-plus']}
                data-test={`${dataTest}-icon-check`}
              />
            )}
          </div>
        ) : (
          <>
            <input
              name={name}
              className={styles.input}
              onChange={onChange}
              type="file"
              accept="image/x-png, image/png, image/jpeg, application/pdf"
              data-test={`${dataTest}-input`}
            />
            <Icon className={styles.icon} data-test={`${dataTest}-icon`} />
          </>
        )}
        <div className={styles.text}>
          <Typography
            className={classes(styles.title, showError && styles.error)}
            size="m"
            data-test={`${dataTest}-title`}
          >
            {customTitle || t(UPLOAD_TYPE[type].title)}
          </Typography>
          {subtitleText && (
            <Typography
              className={styles.subtitle}
              size="s2"
              data-test={`${dataTest}-subtitle`}
            >
              {subtitleText}
            </Typography>
          )}
        </div>
        {value ? (
          <button
            className={styles.delete}
            type="button"
            onClick={handleDelete}
            data-test={`${dataTest}-button`}
          >
            <TrashIcon
              className={classes(showError && styles.errorTrash)}
              data-test={`${dataTest}-icon-trash`}
            />
          </button>
        ) : (
          <PlusIcon
            className={classes(styles.plus, showError && styles.error)}
            data-test={`${dataTest}-icon-plus`}
            height="30px"
            width="30px"
          />
        )}
      </div>
      {showError && (
        <div className={styles['error-wrapper']}>
          <HazardIcon
            className={styles['error-icon']}
            data-test={`${dataTest}-icon-hazard`}
          />
          <Typography className={styles.error} dataTest={`${dataTest}-error`}>
            {error}
          </Typography>
        </div>
      )}
    </div>
  );
});

export default DocumentUpload;
